import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, TextField } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { isNameAllowed } from '../CommonFunctions/CommonFunctions'; 

interface IFieldProps {
  type: string;
  label: string;
  value: string | number | File;
  viewMode: boolean;
  placeHolder?: string;
  multiline?: boolean;
  rows?: number;
  updateFunction: (label: string, value: any, isFile?: boolean) => void;
}

function Field({
  type,
  label,
  value,
  viewMode,
  updateFunction,
  placeHolder,
  multiline,
  rows,
  vendor,
}: IFieldProps & { vendor: string }) {
  const [localValue, setLocalValue] = useState(value);
  const [showError, setShowError] = useState<false | 'empty' | 'special' | 'length'>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    setShowError(false);
  }, [vendor]);

  const handleIconClick = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;

    if (type === 'file' && e.target.files) {
      setLocalValue(e.target.files[0] ? e.target.files[0].name : '');
      updateFunction(label, e.target.files![0], true);
      return;
    }

    setLocalValue(val);
    updateFunction(label, val);

    if (label === 'Connection Name') {
      setShowError(
        val === '' ? 'empty' : val.length > 40 ? 'length' : !isNameAllowed(val) ? 'special' : false,
      );
    } else {
      setShowError(val === '' ? 'empty' : false);
    }

  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'file') return;

    const val = e.target.value;
    updateFunction(label, val);

    if (val === '') {
      setShowError('empty');
    }
  };

  return (
    <>
      <Box style={{ width: '100%', paddingBlock: '0.2rem' }}>
        {type === 'file' ? (
          <>
            <TextField
              style={{ width: '35%' }}
              type="text"
              label={label}
              disabled={viewMode}
              value={localValue ?? ''}
              onBlur={handleBlur}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      onChange={handleChange}
                      disabled={viewMode}
                      readOnly
                    />
                    <IconButton component="span" onClick={handleIconClick}>
                      <AttachFileIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px',
                },
                '@supports (-moz-appearance: none)': {
                  '& .MuiOutlinedInput-input': {
                    padding: '16.5px 2px 16.5px 14px',
                  },
                },
              }}
            />
          </>
        ) : (
          <TextField
            type={type}
            label={label}
            error={!!showError}
            disabled={viewMode}
            value={
              viewMode && (type === 'password' || label === 'Token') ? '••••••••••••••' : localValue
            }
            rows={rows}
            placeholder={placeHolder}
            multiline={multiline}
            required
            onChange={handleChange}
            onBlur={handleBlur}
            autoComplete="off"
            sx={{
              width: '35%',
              color: '#484848',
              '& .MuiOutlinedInput-input': {
                padding: '16.5px 14px',
              },
              '@supports (-moz-appearance: none)': {
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 2px 16.5px 14px',
                },
              },
            }}
            inputProps={{
              style: { color: '#484848' },
              autoComplete: 'new-password',
            }}
          />
        )}
      </Box>

      {showError && (
        <Box style={{ width: '35%', margin: '0 auto' }}>
          <span
            style={{ color: 'red', display: 'block', wordWrap: 'break-word', textAlign: 'left' }}
          >
            {label === 'Connection Name' &&
              showError === 'empty' &&
              'Connection name cannot be empty and must not contain special characters except underscores (_), hyphens (-), and spaces ( ).'}

            {label === 'Connection Name' &&
              showError === 'special' &&
              'Connection name must not contain special characters except underscores (_), hyphens (-), and spaces ( ).'}

            {label === 'Connection Name' &&
              showError === 'length' &&
              'Connection name cannot exceed 40 characters'}

            {label !== 'Connection Name' && showError === 'empty' && `${label} cannot be empty`}
          </span>
        </Box>
      )}
    </>
  );
}

export default Field;
